import { apiFetch } from '@/lib/api';
import type {
  AneelAppeals,
  AneelBidding,
  AneelCaseDetail,
  AneelCaseList,
  AneelCollection,
  AneelConsistency,
  AneelEntities,
  AneelEntity,
  AneelFees,
  AneelNotifications,
  AneelOverview,
  AneelPenalties,
  AneelPositionResult,
  AneelTiList,
  AneelTimeline,
  AneelTipeList,
} from '@/services/types';

/** The second agency. Same envelope, same substrate contract, different corpus. */
export function fetchAneelOverview(): Promise<AneelOverview> {
  return apiFetch<AneelOverview>('/api/v1/aneel/overview');
}

export interface AneelCaseQuery {
  query?: string | null;
  nature?: string | null;
  agency?: string | null;
  trajectory?: string | null;
  band?: string | null;
  year?: number | null;
  /** A code, `ANY` or `NONE` -- see `ANEEL_OUTCOME_FILTER_*` in types. */
  first_outcome?: string | null;
  board_outcome?: string | null;
  /** A collection state the case carries at least one SIGEC row in. */
  credit_status?: string | null;
  /** The company's full 14-digit CNPJ; anything shorter matches nothing. */
  entity?: string | null;
  penalty_type?: string | null;
  limit?: number;
  offset?: number;
}

/**
 * The filter params, without the page bounds.
 *
 * Exported so the CSV export replays the SCREEN's exact query. `limit` and
 * `offset` describe a PAGE and are added by the fetch alone: honouring them in
 * an export would silently write page one to a file and label it the result.
 */
export function aneelCaseParams(query: AneelCaseQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.nature) params.set('nature', query.nature);
  if (query.agency) params.set('agency', query.agency);
  if (query.trajectory) params.set('trajectory', query.trajectory);
  if (query.band) params.set('band', query.band);
  if (query.year) params.set('year', String(query.year));
  if (query.first_outcome) params.set('first_outcome', query.first_outcome);
  if (query.board_outcome) params.set('board_outcome', query.board_outcome);
  if (query.credit_status) params.set('credit_status', query.credit_status);
  if (query.entity) params.set('entity', query.entity);
  if (query.penalty_type) params.set('penalty_type', query.penalty_type);
  return params;
}

export function fetchAneelCases(query: AneelCaseQuery): Promise<AneelCaseList> {
  const params = aneelCaseParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AneelCaseList>(`/api/v1/aneel/cases?${params.toString()}`);
}

/**
 * One infraction notice.
 *
 * `key` is the notice number with `/` replaced by `~` — NOT the digit string
 * ANATEL and CADE use. ANEEL's notice numbers reduce to only 1,024 distinct
 * digit strings across 1,569 cases, so a digits-only key would open an arbitrary
 * one of up to three different cases. `encodeURIComponent` then handles the 31
 * notice numbers that contain a space.
 */
export function fetchAneelCase(key: string): Promise<AneelCaseDetail> {
  return apiFetch<AneelCaseDetail>(`/api/v1/aneel/c/${encodeURIComponent(key)}`);
}

/**
 * What the two instances do with a contested notice.
 *
 * Every share is over the cases an instance ruled on, never over the corpus;
 * the `pending` disclosure carries the denominator the rest sits on. The
 * grouped tables link back to `/aneel/cases` through the outcome filters.
 */
export function fetchAneelAppeals(): Promise<AneelAppeals> {
  return apiFetch<AneelAppeals>('/api/v1/aneel/appeals');
}

/**
 * Per-band charge and final ladders.
 *
 * Most bands refuse the final ladder for sample size — that is the shape of the
 * screen, not a fault in it.
 */
export function fetchAneelPenalties(): Promise<AneelPenalties> {
  return apiFetch<AneelPenalties>('/api/v1/aneel/penalties');
}

export interface AneelPositionQuery {
  amount: number;
  band?: string | null;
  nature?: string | null;
}

/**
 * Where a typed amount sits among the corpus's CHARGES, and a band's, and a
 * nature's -- the same populations the ladders are built from. Never a forecast.
 */
export function fetchAneelPenaltyPosition(query: AneelPositionQuery): Promise<AneelPositionResult> {
  const params = new URLSearchParams();
  params.set('amount', String(query.amount));
  if (query.band) params.set('band', query.band);
  if (query.nature) params.set('nature', query.nature);
  return apiFetch<AneelPositionResult>(`/api/v1/aneel/penalties/position?${params.toString()}`);
}

/**
 * Fine collection against adjudication.
 *
 * `recovery` always refuses: billed and imposed are different quantities from
 * different systems, so dividing them yields something that looks like a
 * recovery rate and is not one.
 */
export function fetchAneelCollection(): Promise<AneelCollection> {
  return apiFetch<AneelCollection>('/api/v1/aneel/collection');
}

export interface AneelFeeQuery {
  query?: string | null;
  /** One credit state; a company matches when at least one of its rows carries it. */
  credit_status?: string | null;
  /** Only companies with a row in a credit state other than settled. */
  exposure?: boolean;
  limit?: number;
  offset?: number;
}

/** Filter params without the page bounds, so the export replays the screen. */
export function aneelFeeParams(query: AneelFeeQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.credit_status) params.set('credit_status', query.credit_status);
  if (query.exposure) params.set('exposure', 'true');
  return params;
}

/**
 * The TFSEE inspection fee — a levy every concessionaire pays, NOT a penalty.
 *
 * A different corpus from every other ANEEL endpoint: 447,701 billing records
 * over 3,470 companies across twenty-one years. Never pooled with enforcement.
 */
export function fetchAneelFees(query: AneelFeeQuery): Promise<AneelFees> {
  const params = aneelFeeParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AneelFees>(`/api/v1/aneel/fees?${params.toString()}`);
}

/**
 * ANEEL's two non-infraction tracks: intimations and edital penalties.
 *
 * `ti_board` and `ti_money` both refuse, for different reasons: the board column
 * is empty on every row, and the table has no amount column at all.
 */
export function fetchAneelBidding(): Promise<AneelBidding> {
  return apiFetch<AneelBidding>('/api/v1/aneel/bidding');
}

export interface AneelTiQuery {
  nature?: string | null;
  penalty_type?: string | null;
  query?: string | null;
  limit?: number;
  offset?: number;
}

export function aneelTiParams(query: AneelTiQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.nature) params.set('nature', query.nature);
  if (query.penalty_type) params.set('penalty_type', query.penalty_type);
  if (query.query) params.set('query', query.query);
  return params;
}

/** One page of intimations, with the track's filter options. */
export function fetchAneelTiRows(query: AneelTiQuery): Promise<AneelTiList> {
  const params = aneelTiParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AneelTiList>(`/api/v1/aneel/bidding/ti?${params.toString()}`);
}

export interface AneelTipeQuery {
  penalty_type?: string | null;
  /** Only rows typed as a bidding restriction. A row is a stage, never a status. */
  sanctions_only?: boolean | null;
  query?: string | null;
  limit?: number;
  offset?: number;
}

/** Filter params without the page bounds, so the export replays the screen. */
export function aneelTipeParams(query: AneelTipeQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.penalty_type) params.set('penalty_type', query.penalty_type);
  if (query.sanctions_only) params.set('sanctions_only', 'true');
  if (query.query) params.set('query', query.query);
  return params;
}

/** One page of TIPE detail rows -- an event log, so ROW grain. */
export function fetchAneelTipeRows(query: AneelTipeQuery): Promise<AneelTipeList> {
  const params = aneelTipeParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AneelTipeList>(`/api/v1/aneel/bidding/tipe?${params.toString()}`);
}

/**
 * Recorded durations per step, plus the cohort table.
 *
 * `outcome_trend` always refuses: a rate by notice year measures how old a case
 * is, not how ANEEL decides.
 */
export function fetchAneelTimeline(): Promise<AneelTimeline> {
  return apiFetch<AneelTimeline>('/api/v1/aneel/timeline');
}

/**
 * The inspection funnel upstream of every other ANEEL screen.
 *
 * `other_track` refuses: none of the non-escalated inspections links to
 * anything here, because no such instrument is loaded.
 */
export function fetchAneelNotifications(): Promise<AneelNotifications> {
  return apiFetch<AneelNotifications>('/api/v1/aneel/notifications');
}

/**
 * Decided cases of the same nature and band that ended differently.
 *
 * Not an adjudicated queue: ANEEL publishes no reasoning text, so no
 * contradiction model has judged any of these. The payload's `verdict_layer`
 * carries that refusal.
 */
export interface AneelConsistencyQuery {
  nature?: string | null;
  band?: string | null;
  /** A body that issued EITHER side's notice. */
  agency?: string | null;
  /** true: different bodies only; false: same body only; null: both. */
  cross_agency?: boolean | null;
  /** The ordered outcome combination, e.g. `MAINTAINED/REDUCED`. */
  combination?: string | null;
  limit?: number;
  offset?: number;
}

/** Filter params without the page bounds, so the export replays the screen. */
export function aneelConsistencyParams(query: AneelConsistencyQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.nature) params.set('nature', query.nature);
  if (query.band) params.set('band', query.band);
  if (query.agency) params.set('agency', query.agency);
  if (query.cross_agency === true || query.cross_agency === false) {
    params.set('cross_agency', String(query.cross_agency));
  }
  if (query.combination) params.set('combination', query.combination);
  return params;
}

export function fetchAneelConsistency(query: AneelConsistencyQuery): Promise<AneelConsistency> {
  const params = aneelConsistencyParams(query);
  params.set('limit', String(query.limit ?? 25));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AneelConsistency>(`/api/v1/aneel/consistency?${params.toString()}`);
}

export interface AneelEntityQuery {
  query?: string | null;
  /** `enforcement`, `ti`, `tipe` or `fees`. */
  track?: string | null;
  min_cases?: number | null;
  exposure?: boolean | null;
  limit?: number;
  offset?: number;
}

/** Filter params without the page bounds, so the export replays the screen. */
export function aneelEntityParams(query: AneelEntityQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.track) params.set('track', query.track);
  if (query.min_cases) params.set('min_cases', String(query.min_cases));
  if (query.exposure) params.set('exposure', 'true');
  return params;
}

/**
 * The company directory, by name, never ranked.
 *
 * Presence per track and a fee-exposure flag per company; the `directory`
 * answer carries the measured spread the profile's explainer once hardcoded.
 */
export function fetchAneelEntities(query: AneelEntityQuery): Promise<AneelEntities> {
  const params = aneelEntityParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<AneelEntities>(`/api/v1/aneel/entities?${params.toString()}`);
}

/**
 * One company across all four ANEEL tracks.
 *
 * `cnpj` is the full 14 digits — a complete natural key here, 3,803 distinct
 * values over 3,803 companies. A prefix 404s rather than opening an arbitrary
 * one of several companies.
 *
 * Most of this payload refuses most of the time: 2,675 of 3,803 companies
 * appear in exactly one track, and 2,375 of them only in the fee ledger.
 */
export function fetchAneelEntity(cnpj: string): Promise<AneelEntity> {
  return apiFetch<AneelEntity>(`/api/v1/aneel/e/${encodeURIComponent(cnpj)}`);
}
